import { useEffect, type RefObject } from 'react'
import L from 'leaflet'
import 'leaflet-polylinedecorator'
import { setMapInstance } from '@/core/commands/mapInstance'
import { basemapSource } from '@/features/network-visualization/basemap'

/**
 * Creates the Leaflet map and publishes it (Phase 1J).
 *
 * This is `views/composables/useMapState.js`, moved. Every value is that file's
 * — the centre, the zoom, the tile URL, the control's corner — because the
 * point of this step is that the map is created by React and is otherwise
 * identical.
 *
 * It publishes through `core/commands/mapInstance`, which the four consumers
 * already read from: the two cost-layer composables and the two overlay
 * components stopped taking the map as a prop or an argument in the commits
 * before this one. None of them changes here, and that is the whole design —
 * the producer swapped and the consumers did not notice.
 *
 * Runs once, and tears the map down on unmount. `L.Map.remove()` is the only
 * teardown Leaflet has, and the null publish that follows it matters: a
 * consumer holding a removed map throws on its next call.
 */
export function useLeafletMap(container: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const element = container.current
    if (!element) return

    const map = L.map(element, {
      center: [39.8283, -98.5795], // Center of US
      zoom: 5,
      zoomControl: false, // added below, in the bottom-right corner
      // Required by both tile providers' terms; it was `false`, which is why
      // nothing on screen said where the map came from. Leaflet stacks it under
      // the zoom control in the same corner.
      attributionControl: true,
      // Draw vectors onto one canvas rather than one SVG <path> element each.
      // Lanes are the bulk of what this map draws, and a DOM node per lane is
      // what makes a large network unusable rather than merely slow.
      preferCanvas: true,
    })

    // Bottom-right, not Leaflet's top-left: the onboarding card owns the
    // bottom-left, the Quick Filters panel and the Cost layer card the
    // top-right, and the top-left is where the onboarding card reaches once the
    // page is the height of the viewport.
    L.control.zoom({ position: 'bottomright' }).addTo(map)

    // Shaded-relief terrain by default, CARTO's flat light cartography when
    // there is a key for it. Why terrain, and why not the other two terrain
    // providers, is in `basemap.ts`.
    const basemap = basemapSource(import.meta.env.VITE_CARTO_API_KEY)
    L.tileLayer(basemap.url, {
      attribution: basemap.attribution,
      maxZoom: basemap.maxZoom,
      // Only when the source shards. Passing `undefined` would replace
      // Leaflet's own default and break `{s}` for the sources that use it.
      ...(basemap.subdomains ? { subdomains: basemap.subdomains } : {}),
    }).addTo(map)

    const facilitiesLayer = L.layerGroup().addTo(map)
    const lanesLayer = L.layerGroup().addTo(map)

    setMapInstance({ map, facilitiesLayer, lanesLayer, container: element })

    return () => {
      // Unpublish FIRST, so every consumer drops its overlay while the map it
      // is attached to still exists. Removing the map first would leave them
      // calling `removeLayer` on a torn-down map.
      setMapInstance(null)
      map.remove()
    }
  }, [container])
}
